import { PlusIcon } from "@heroicons/react/24/outline";
import { arrayHasItems, formatCurrencyUsdFixedTwo } from "../../../helpers/common"
import { useState, useMemo } from "react";
import { SplitExceptionsDrawer } from "./SplitExceptionsDrawer";
import { ApprovalLogInformationBadge } from "./ApprovalLogInformationBadge";
import { DraftPoSplitDecisionModal } from "./DraftPoSplitDecisionModal";
import { LoaderSpinner } from "../../LoaderSpinner";

const toForecastDemandProductId = (variant) =>
    Number.parseInt(String(variant?.id ?? ""), 10);

const roundUnitsToTwo = (value) =>
    parseFloat((Number(value) || 0).toFixed(2));

const sumVariantUnits = (variants) =>
    roundUnitsToTwo(
        (variants || []).reduce((sum, variant) => sum + (Number(variant.units) || 0), 0)
    );

const formatUnitsLabel = (units) =>
    units > 1 ? `${units} units` : `${units} unit`;

const getVariantLineCash = (variant, totalCash, allVariants, cashById) => {
    const id = toForecastDemandProductId(variant);
    if (
        Number.isFinite(id) &&
        id > 0 &&
        cashById &&
        Object.prototype.hasOwnProperty.call(cashById, id)
    ) {
        return Number(cashById[id]) || 0;
    }
    const totalUnits =
        (allVariants || []).reduce((sum, entry) => sum + (Number(entry.units) || 0), 0) || 1;
    return totalCash * ((Number(variant.units) || 0) / totalUnits);
};

const buildDraftPoDisplayEntries = (draftedPo, cashById) => {
    const variants = arrayHasItems(draftedPo?.variants) ? draftedPo.variants : [];
    const exceptionSet = new Set(
        (draftedPo?.exceptionForecastDemandProductIds || [])
            .map((id) => Number.parseInt(String(id ?? ""), 10))
            .filter((id) => Number.isFinite(id) && id > 0)
    );
    const totalCash = Number(draftedPo?.cashRequired) || 0;
    const poAVariants = variants.filter((variant) => !exceptionSet.has(toForecastDemandProductId(variant)));
    const poBVariants = variants.filter((variant) => exceptionSet.has(toForecastDemandProductId(variant)));
    const sumCash = (list) =>
        list.reduce(
            (sum, variant) => sum + getVariantLineCash(variant, totalCash, variants, cashById),
            0
        );

    const entries = [];
    if (poAVariants.length > 0 && draftedPo?.poNumber) {
        entries.push({
            poNumber: draftedPo.poNumber,
            skuCount: poAVariants.length,
            totalUnits: sumVariantUnits(poAVariants),
            totalCash: sumCash(poAVariants),
        });
    }
    if (poBVariants.length > 0) {
        entries.push({
            poNumber: draftedPo.splitPoNumber || "PO B (Drafted)",
            skuCount: poBVariants.length,
            totalUnits: sumVariantUnits(poBVariants),
            totalCash: sumCash(poBVariants),
        });
    }
    return entries;
};

const buildStagedDraftedPo = (group, draftResult = {}) => ({
    id: group.id,
    groupName: group.groupName,
    supplierName: group.supplierName || "",
    variants: group.variants || [],
    cashRequired: group.replenishmentCashRequired ?? 0,
    splittedCount: 0,
    exceptionForecastDemandProductIds: [],
    allowSplit: true,
    purchaseOrderId: draftResult.purchaseOrderId || null,
    poNumber: draftResult.poNumber || null,
    splitPurchaseOrderId: null,
    splitPoNumber: null,
});

export const ReviewQueueApprovedBuys = ({
    draftedLogs,
    approvedLogs,
    draftedPos,
    onDraftGroup,
    onReturnToQueue,
    isPersistingSplit = false,
    replenishmentCashByForecastProductId = {},
    onPersistDraftedSplit,
    marginFromSidebar,
}) => {

    const [splitExceptionsDrawerOpen, setSplitExceptionsDrawerOpen] = useState({
        opened: false,
        groupId: null,
    });
    const [pendingDraftDecisionGroup, setPendingDraftDecisionGroup] = useState(null);
    const [splitDrawerDraftedPo, setSplitDrawerDraftedPo] = useState(null);
    const [draftingGroupId, setDraftingGroupId] = useState(null);
    const [returningGroupId, setReturningGroupId] = useState(null);

    const activeSplitDraftedPo = useMemo(() => {
        const groupId = splitExceptionsDrawerOpen.groupId;
        if (!groupId) return null;
        const fromList = (draftedPos || []).find((po) => po.id === groupId);
        if (fromList) return fromList;
        if (splitDrawerDraftedPo?.id === groupId) return splitDrawerDraftedPo;
        return null;
    }, [draftedPos, splitDrawerDraftedPo, splitExceptionsDrawerOpen.groupId]);

    const closeSplitDrawer = () => {
        setSplitExceptionsDrawerOpen({
            opened: false,
            groupId: null,
        });
        setSplitDrawerDraftedPo(null);
    };

    const openDraftDecisionModal = (groupId) => {
        if (!groupId) return;
        const group = (draftedLogs || []).find((draftedLog) => draftedLog.id === groupId);
        if (!group) return;
        setPendingDraftDecisionGroup(group);
    };

    const closeDraftDecisionModal = () => {
        if (draftingGroupId) return;
        setPendingDraftDecisionGroup(null);
    };

    const finalizeDraftDecision = async (shouldOpenSplit) => {
        if (!pendingDraftDecisionGroup || typeof onDraftGroup !== 'function') return;
        const group = pendingDraftDecisionGroup;
        setDraftingGroupId(group.id);
        try {
            const draftResult = await onDraftGroup(group, { shouldOpenSplit });
            setPendingDraftDecisionGroup(null);
            if (shouldOpenSplit && draftResult) {
                setSplitDrawerDraftedPo(buildStagedDraftedPo(group, draftResult));
                setSplitExceptionsDrawerOpen({
                    opened: true,
                    groupId: group.id,
                });
            }
        } catch (error) {
            console.error(error);
        } finally {
            setDraftingGroupId(null);
        }
    };

    const returnGroupToQueue = async (groupId) => {
        if (!groupId || typeof onReturnToQueue !== 'function') return;
        const draftedPo = (draftedPos || []).find((po) => po.id === groupId);
        if (!draftedPo) return;
        setReturningGroupId(groupId);
        try {
            await onReturnToQueue(draftedPo);
        } finally {
            setReturningGroupId(null);
        }
    };

    return (
        <>
            <div className="mt-4 grid gap-4 xl:grid-cols-[minmax(0,1.15fr)_minmax(320px,0.85fr)]">
                <div className="rounded-lg border border-border bg-background p-4">
                    <div className="mb-3 flex items-center justify-between gap-3">
                        <div>
                            <h3 className="text-sm font-semibold text-primary">
                                Drafted PO candidates by product
                            </h3>
                            <p className="mt-1 text-xs text-gray-600">
                                Approved BUY rows are grouped by product so planners can review one draft purchase order per product before a manual send in Eadin.
                            </p>
                        </div>
                    </div>
                    <div className="space-y-3 max-h-[1200px] overflow-y-auto">
                        {!arrayHasItems(draftedLogs) ? (
                            <div className="rounded-md border border-dashed border-border bg-white px-4 py-6 text-sm text-gray-600 whitespace-break-spaces">
                                No approved BUY rows are ready to roll into product-grouped draft PO review yet.
                            </div>
                        ) : (
                            <>
                                {draftedLogs.map((draftedLog) => {
                                    const variantsCount = arrayHasItems(draftedLog.variants) ? draftedLog.variants.length : 0;
                                    const variantsUnits = arrayHasItems(draftedLog.variants) ? sumVariantUnits(draftedLog.variants) : 0;
                                    const variantsUnitsString = formatUnitsLabel(variantsUnits);
                                    const cashString = formatCurrencyUsdFixedTwo(draftedLog.replenishmentCashRequired ?? 0);
                                    return (
                                        <div className="rounded-md border border-border bg-white p-3" key={draftedLog.id}>
                                            <div className="flex flex-wrap items-center justify-between gap-3">
                                                <div>
                                                    <p className="text-sm font-medium text-primary">
                                                        {draftedLog.groupName || ""}
                                                    </p>
                                                    <p className="mt-1 text-xs text-gray-600">
                                                        {variantsCount || 0} {`${variantsCount > 1 ? "SKUs" : "SKU"}`} · {draftedLog.supplierName || ""}
                                                    </p>
                                                </div>
                                                <div className="flex flex-wrap items-center gap-2">
                                                    <span
                                                        className="inline-flex items-center justify-center w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden [a&]:hover:bg-primary/90 rounded-md border border-border bg-background px-2 py-1 text-[11px] font-medium text-primary shadow-none"
                                                    >
                                                        {variantsUnitsString}
                                                    </span>
                                                    <span
                                                        className="inline-flex items-center justify-center w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden [a&]:hover:bg-primary/90 rounded-md border border-border bg-background px-2 py-1 text-[11px] font-medium text-primary shadow-none"
                                                    >
                                                        {cashString} cash
                                                    </span>
                                                    <button
                                                        onClick={() => {
                                                            openDraftDecisionModal(draftedLog.id);
                                                        }}
                                                        disabled={Boolean(draftingGroupId)}
                                                        className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive border bg-transparent shadow-xs hover:bg-gray-50 dark:bg-transparent dark:border-input dark:hover:bg-input/50 py-0.5 has-[>svg]:px-3 h-auto rounded-md px-3"
                                                    >
                                                        <PlusIcon className="h-4 w-4 text-primary" />
                                                        <span>Save Draft</span>
                                                    </button>
                                                </div>
                                            </div>
                                            {arrayHasItems(draftedLog.variants) && (
                                                <div className="mt-3 space-y-2">
                                                    {draftedLog.variants.map((variant) => {
                                                        return (
                                                            <div className="flex items-center justify-between gap-3 rounded-md border border-border/70 px-3 py-2 text-sm" key={variant.id}>
                                                                <div className="min-w-0">
                                                                    <p className="font-medium text-primary">
                                                                        {variant.sku || ""}
                                                                    </p>
                                                                    <p className="truncate text-xs text-gray-600 whitespace-break-spaces">
                                                                        {variant.notes || ""}
                                                                    </p>
                                                                </div>
                                                                <div className="text-xs font-medium text-primary">
                                                                    {variant.units || 0} {`${variant.units > 1 ? "units" : "unit"}`}
                                                                </div>
                                                            </div>
                                                        )
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    )
                                })}
                            </>
                        )}
                    </div>
                </div>
                <aside className="space-y-4">
                    <div className="rounded-lg border border-border bg-white p-4">
                        <div className="flex items-center justify-between gap-3">
                            <div>
                                <h3 className="text-sm font-semibold text-primary">
                                    Drafted PO review
                                </h3>
                                <p className="mt-1 text-xs text-gray-600">
                                    Products moved with Draft PO appear here so the merchandiser can review what has already been staged and split one drafted PO at a time only when an exception SKU needs separate treatment.
                                </p>
                            </div>
                        </div>
                        <div className="mt-4 space-y-3">
                            {!arrayHasItems(draftedPos) ? (
                                <div className="rounded-md border border-dashed border-border bg-white px-4 py-6 text-sm text-gray-600 whitespace-break-spaces">
                                    Nothing has been drafted yet. Clicking Draft PO now moves a product group into this review area.
                                </div>
                            ) : (
                                <>
                                    {draftedPos.map((draftedPo) => {
                                        const cashString = formatCurrencyUsdFixedTwo(draftedPo.cashRequired ?? 0);
                                        const variantsCount = arrayHasItems(draftedPo.variants) ? draftedPo.variants.length : 0;
                                        const variantsUnits = arrayHasItems(draftedPo.variants) ? sumVariantUnits(draftedPo.variants) : 0;
                                        const variantsUnitsString = formatUnitsLabel(variantsUnits);
                                        const splittedCount = draftedPo.splittedCount ?? 0;
                                        const canSplitDraft = draftedPo.allowSplit !== false;
                                        const poEntries = buildDraftPoDisplayEntries(
                                            draftedPo,
                                            replenishmentCashByForecastProductId
                                        );
                                        const isSplitPersistingForPo =
                                            isPersistingSplit &&
                                            splitExceptionsDrawerOpen.groupId === draftedPo.id;
                                        return (
                                            <div className="rounded-md border border-border bg-white p-3" key={draftedPo.id}>
                                                <div 
                                                    className="flex flex-col items-start justify-between gap-3"
                                                >
                                                    <div className="w-full">
                                                        <div className="flex flex-wrap items-center gap-2">
                                                            <p className="text-sm font-medium text-primary">
                                                                {draftedPo.groupName || ""}
                                                            </p>
                                                            <span
                                                                className="inline-flex items-center justify-center border w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden [a&]:hover:bg-primary/90 rounded-md border-transparent bg-amber-50 px-2 py-1 text-[10px] font-medium text-amber-700 shadow-none"
                                                            >
                                                                Draft
                                                            </span>
                                                            {!canSplitDraft ? (
                                                                <span
                                                                    className="inline-flex items-center justify-center border w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden [a&]:hover:bg-primary/90 rounded-md border border-border bg-background px-2 py-1 text-[10px] font-medium text-primary shadow-none"
                                                                >
                                                                    No split
                                                                </span>
                                                            ) : null}
                                                            {splittedCount > 0 ? (
                                                                <span
                                                                    className="inline-flex items-center justify-center border w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden [a&]:hover:bg-primary/90 rounded-md border-transparent bg-amber-50 px-2 py-1 text-[10px] font-medium text-amber-700 shadow-none"
                                                                >
                                                                    {splittedCount} exception SKU{splittedCount > 1 ? "s" : ""}
                                                                </span>
                                                            ) : null}
                                                        </div>
                                                        <p className="mt-1 text-xs text-gray-600">
                                                            {variantsCount || 0} {`${variantsCount > 1 ? "SKUs" : "SKU"}`} · {draftedPo.supplierName || ""}
                                                        </p>
                                                        {arrayHasItems(poEntries) ? (
                                                            <div className="mt-3 flex flex-wrap gap-2">
                                                                {poEntries.map((entry) => (
                                                                    <div
                                                                        key={`${draftedPo.id}-${entry.poNumber}`}
                                                                        className="rounded-md border border-border bg-background px-3 py-2"
                                                                    >
                                                                        <div className="flex items-center gap-2">
                                                                            <span className="text-xs font-semibold uppercase tracking-[0.12em] text-primary">
                                                                                {entry.poNumber}
                                                                            </span>
                                                                            <span className="inline-flex items-center justify-center border w-fit whitespace-nowrap shrink-0 rounded-md border-transparent bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 shadow-none">
                                                                                Draft
                                                                            </span>
                                                                        </div>
                                                                        <p className="mt-1 text-xs text-gray-600">
                                                                            {entry.skuCount} {entry.skuCount > 1 ? "SKUs" : "SKU"} · {entry.totalUnits} {entry.totalUnits === 1 ? "unit" : "units"} · {formatCurrencyUsdFixedTwo(entry.totalCash)}
                                                                        </p>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        ) : null}
                                                    </div>

                                                    <div className="flex flex-wrap items-center gap-2">
                                                        <span className="inline-flex items-center justify-center w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden [a&]:hover:bg-primary/90 rounded-md border border-border bg-white px-2 py-1 text-[11px] font-medium text-primary shadow-none">
                                                            {variantsUnitsString}
                                                        </span>
                                                        <span className="inline-flex items-center justify-center w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden [a&]:hover:bg-primary/90 rounded-md border border-border bg-white px-2 py-1 text-[11px] font-medium text-primary shadow-none">
                                                            {cashString} cash
                                                        </span>
                                                        <span className="inline-flex items-center justify-center w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden [a&]:hover:bg-primary/90 rounded-md border border-border bg-white px-2 py-1 text-[11px] font-medium text-primary shadow-none">
                                                            {poEntries.length} PO{poEntries.length > 1 ? "s" : ""}
                                                        </span>
                                                        {canSplitDraft ? (
                                                            <button
                                                                onClick={() => {
                                                                    setSplitExceptionsDrawerOpen({
                                                                        opened: true,
                                                                        groupId: draftedPo.id,
                                                                    });
                                                                }}
                                                                disabled={
                                                                    isSplitPersistingForPo ||
                                                                    returningGroupId === draftedPo.id ||
                                                                    Boolean(draftingGroupId)
                                                                }
                                                                className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive border bg-transparent shadow-xs hover:bg-gray-50 dark:bg-transparent dark:border-input dark:hover:bg-input/50 py-0.5 has-[>svg]:px-3 h-auto rounded-md px-3 min-w-[148px]"
                                                            >
                                                                {isSplitPersistingForPo ? (
                                                                    <LoaderSpinner width="20" height="20" border="3" colorSpinner="gray" />
                                                                ) : (
                                                                    splittedCount > 0 ? "Edit exception split" : "Split exceptions"
                                                                )}
                                                            </button>
                                                        ) : null}
                                                        <button
                                                            onClick={() => {
                                                                returnGroupToQueue(draftedPo.id);
                                                            }}
                                                            disabled={returningGroupId === draftedPo.id || Boolean(draftingGroupId)}
                                                            className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive border bg-transparent shadow-xs hover:bg-gray-50 dark:bg-transparent dark:border-input dark:hover:bg-input/50 py-0.5 has-[>svg]:px-3 h-auto rounded-md px-3 min-w-[148px]"
                                                        >
                                                            {returningGroupId === draftedPo.id ? (
                                                                <LoaderSpinner width="20" height="20" border="3" colorSpinner="gray" />
                                                            ) : (
                                                                "Return to candidates"
                                                            )}
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        )
                                    })}
                                </>
                            )}
                        </div>
                    </div>
                    <div className="rounded-lg border border-border bg-white p-4">
                        <div className="flex items-center justify-between gap-3 mb-3">
                            <div>
                                <h3 className="text-sm font-semibold text-primary">
                                    Approval log
                                </h3>
                                <p className="mt-1 text-xs text-gray-600">
                                    Every Approved change is tracked here so planners can review recent OTB decisions.
                                </p>
                            </div>
                        </div>
                        <div className="space-y-3 max-h-[1000px] overflow-y-auto">
                            {!arrayHasItems(approvedLogs) ? (
                                <div className="rounded-md border border-dashed border-border bg-white px-4 py-6 text-sm text-gray-600 whitespace-break-spaces">
                                    No OTB approval decisions have been logged yet.
                                </div>
                            ) : (
                                <>
                                    {approvedLogs.map((approvedLog) => {
                                        return (
                                            <div className="rounded-md border border-border bg-white p-3" key={approvedLog.id}>
                                                <div className="mb-2 flex items-center gap-2">
                                                    <span className="bg-gray-50 border border-gray-200 text-primary text-xs font-medium px-2 py-1 rounded-lg">
                                                        {approvedLog.productSku || "N/A"}
                                                    </span>
                                                    <span className="inline-flex items-center justify-center border w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden [a&]:hover:bg-primary/90 rounded-md px-2 py-1 text-[11px] font-medium shadow-none border-transparent bg-emerald-50 text-emerald-700">
                                                        {approvedLog.status || "Approved"}
                                                    </span>
                                                </div>
                                                <p className="text-sm font-medium text-primary">
                                                    {approvedLog.productLine || ""}
                                                </p>
                                                <div className="mt-3 grid gap-2 text-xs text-gray-600 sm:grid-cols-2">
                                                    <ApprovalLogInformationBadge
                                                        label="Risk flag"
                                                        value={approvedLog.riskFlag || "N/A"}
                                                    />
                                                    <ApprovalLogInformationBadge
                                                        label="Units to order"
                                                        value={approvedLog.unitsToOrder ?? "N/A"}
                                                    />
                                                    <ApprovalLogInformationBadge
                                                        label="Planner context"
                                                        value={approvedLog.plannerContext || ""}
                                                    />
                                                    <ApprovalLogInformationBadge
                                                        label="Timestamp"
                                                        value={approvedLog.timestamp || ""}
                                                    />
                                                </div>
                                            </div>
                                        )
                                    })}
                                </>
                            )}
                        </div>
                    </div>
                </aside>
            </div>
            <DraftPoSplitDecisionModal
                isOpen={Boolean(pendingDraftDecisionGroup)}
                group={pendingDraftDecisionGroup}
                isLoading={Boolean(draftingGroupId)}
                onCancel={closeDraftDecisionModal}
                onKeepOnePo={() => finalizeDraftDecision(false)}
                onSplitPo={() => finalizeDraftDecision(true)}
            />
            <SplitExceptionsDrawer
                open={splitExceptionsDrawerOpen.opened}
                marginFromSidebar={marginFromSidebar}
                onClose={closeSplitDrawer}
                draftedPo={activeSplitDraftedPo}
                replenishmentCashByForecastProductId={replenishmentCashByForecastProductId}
                onPersistSplit={(exceptionIds) => {
                    if (activeSplitDraftedPo && typeof onPersistDraftedSplit === 'function') {
                        return onPersistDraftedSplit(activeSplitDraftedPo, exceptionIds);
                    }
                    return Promise.resolve();
                }}
                isPersisting={isPersistingSplit}
            />
        </>
    )
}